import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, setDoc, updateDoc, doc, serverTimestamp, where, getDocs, writeBatch } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useFilterBar } from '../hooks/useFilterBar';
import FilterActions from '../components/common/FilterActions';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { useAuth } from '../components/auth/AuthContext';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import { logUserAction } from '../utils/logger';
import CampaignBuilder from '../components/forms/CampaignBuilder';
import { formatDistanceToNow } from 'date-fns';
import { buildEngagementFilter, filterAudienceEvents } from '../utils/emailBotDetection';
import { buildDeferredStatus } from '../utils/emailDeferredStatus';
import { canViewAllEmail } from '../utils/emailAccess';
import { useEmailCampaignData } from '../hooks/useEmailCampaignData';
import { generateTimestampId } from '../utils/documentIdUtils';

const EmailCampaignPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { can } = usePermissions(currentUser);
  const viewAllEmail = canViewAllEmail(currentUser, can);
  const { campaigns, events, loading, error: loadError } = useEmailCampaignData(currentUser, viewAllEmail);
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [toast, setToast] = useState(null);
  const fb = useFilterBar({
    filter: 'active',
    search: '',
    creatorFilter: 'all',
    sortField: null,
    sortDirection: 'asc',
  });

  useEffect(() => {
    setSegments([]);
    const fail = error => {
      console.error('Failed to load campaign resources:', error);
      setToast({ message: `Could not load campaign resources: ${error.message}`, type: 'error' });
    };
    // Load templates
    const templatesUnsub = onSnapshot(
      query(collection(db, collectionPath('emailTemplates'))),
      (snap) => {
        setTemplates(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, fail
    );
    
    // Load segments
    const segmentSource = collection(db, collectionPath('emailSegments'));
    const segmentsUnsub = onSnapshot(
      viewAllEmail ? segmentSource : query(segmentSource, where('createdBy', '==', currentUser.uid)),
      (snap) => {
        setSegments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, fail
    );
    
    return () => {
      templatesUnsub();
      segmentsUnsub();
    };
  }, [currentUser.uid, viewAllEmail]);

  useEffect(() => {
    if (loadError) setToast({ message: `Could not load campaigns: ${loadError.message}`, type: 'error' });
  }, [loadError]);

  // Handle column sorting
  const handleSort = (field) => {
    if (fb.draft.sortField === field) {
      fb.setDraft('sortDirection', fb.draft.sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      fb.setDraft('sortField', field);
      fb.setDraft('sortDirection', 'asc');
    }
  };

  const filteredCampaigns = useMemo(() => {
    let filtered = campaigns;
    
    // Apply status filter
    if (fb.applied.filter === 'active') {
      filtered = campaigns.filter(c => !c.isArchived);
    } else if (fb.applied.filter === 'archived') {
      filtered = campaigns.filter(c => c.isArchived);
    } else if (fb.applied.filter === 'all') {
      filtered = campaigns;
    } else if (fb.applied.filter === 'sent') {
      // "Sent" tab includes sent, partial_failure, and failed campaigns
      filtered = campaigns.filter(c => (c.status === 'sent' || c.status === 'partial_failure' || c.status === 'failed') && !c.isArchived);
    } else {
      filtered = campaigns.filter(c => c.status === fb.applied.filter && !c.isArchived);
    }
    
    // Apply creator filter
    if (fb.applied.creatorFilter === 'mine') {
      filtered = filtered.filter(c => c.createdBy === currentUser?.uid);
    } else {
      // "All Campaigns" - hide private campaigns from other users
      filtered = filtered.filter(c => 
        !c.isPrivate || !c.createdBy || c.createdBy === currentUser?.uid
      );
    }
    
    // Apply search filter
    if (fb.applied.search.trim()) {
      const searchLower = fb.applied.search.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(searchLower) ||
        c.subject?.toLowerCase().includes(searchLower) ||
        c.fromName?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    if (fb.applied.sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;
        
        if (fb.applied.sortField === 'name') {
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
        } else if (fb.applied.sortField === 'createdBy') {
          aValue = (a.createdByName || a.createdBy || 'zzz').toLowerCase();
          bValue = (b.createdByName || b.createdBy || 'zzz').toLowerCase();
        } else if (fb.applied.sortField === 'createdAt') {
          aValue = a.createdAt?.toDate?.() || new Date(0);
          bValue = b.createdAt?.toDate?.() || new Date(0);
        }
        
        if (aValue < bValue) return fb.applied.sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return fb.applied.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [campaigns, fb.applied.filter, fb.applied.creatorFilter, fb.applied.search, fb.applied.sortField, fb.applied.sortDirection, currentUser]);
  
  // Calculate counts for filter dropdown (context-aware based on current status filter)
  const myCampaignsCount = useMemo(() => {
    const baseFilter = campaigns.filter(c => c.createdBy === currentUser?.uid);
    if (fb.applied.filter === 'active') return baseFilter.filter(c => !c.isArchived).length;
    if (fb.applied.filter === 'archived') return baseFilter.filter(c => c.isArchived).length;
    if (fb.applied.filter === 'all') return baseFilter.length;
    return baseFilter.filter(c => c.status === fb.applied.filter && !c.isArchived).length;
  }, [campaigns, fb.applied.filter, currentUser]);
  
  const allCampaignsCount = useMemo(() => {
    const baseFilter = campaigns.filter(c => !c.isPrivate || !c.createdBy || c.createdBy === currentUser?.uid);
    if (fb.applied.filter === 'active') return baseFilter.filter(c => !c.isArchived).length;
    if (fb.applied.filter === 'archived') return baseFilter.filter(c => c.isArchived).length;
    if (fb.applied.filter === 'all') return baseFilter.length;
    return baseFilter.filter(c => c.status === fb.applied.filter && !c.isArchived).length;
  }, [campaigns, fb.applied.filter, currentUser]);
  
  // Create lookup maps
  const templatesById = useMemo(() => {
    const map = new Map();
    templates.forEach(t => map.set(t.id, t));
    return map;
  }, [templates]);
  
  const segmentsById = useMemo(() => {
    const map = new Map();
    segments.forEach(s => map.set(s.id, s));
    return map;
  }, [segments]);
  
  // Calculate real-time stats from events (with bot filtering)
  const campaignStats = useMemo(() => {
    const statsMap = new Map();
    
    campaigns.forEach(campaign => {
      // BCC/verification copies (fromEmail, replyTo, campaignBccEmails setting) are
      // internal addresses, not real audience members — exclude from every stat.
      const campaignEvents = filterAudienceEvents(events.filter(e => e.campaignId === campaign.id));
      
      // Apply bot/scanner detection per campaign
      const { isHumanEngagement } = buildEngagementFilter(campaignEvents);
      
      const uniqueOpensSet = new Set();
      const uniqueClicksSet = new Set();
      let sent = 0;
      let delivered = 0;
      let totalOpens = 0;
      let totalClicks = 0;
      let bounces = 0;
      let unsubscribes = 0;
      
      campaignEvents.forEach(event => {
        if (event.eventType === 'sent') sent++;
        if (event.eventType === 'delivered') { delivered++; }
        if (event.eventType === 'opened' && isHumanEngagement(event)) {
          totalOpens++;
          if (event.recipientEmail) uniqueOpensSet.add(event.recipientEmail);
        }
        if (event.eventType === 'clicked' && isHumanEngagement(event)) {
          totalClicks++;
          if (event.recipientEmail) uniqueClicksSet.add(event.recipientEmail);
        }
        if (event.eventType === 'bounced') { bounces++; }
        if (event.eventType === 'unsubscribed') unsubscribes++;
      });

      // Pending retry = deferred AND not yet resolved AND most recent deferred
      // is still within SendGrid's 72h retry window (+ buffer). After that we
      // treat the recipient as expired so the count doesn't get stuck forever.
      const { pendingRetryEmails } = buildDeferredStatus(campaignEvents);
      const pendingRetry = pendingRetryEmails.size;
      
      statsMap.set(campaign.id, {
        sent,
        delivered,
        opens: totalOpens,
        uniqueOpens: uniqueOpensSet.size,
        clicks: totalClicks,
        uniqueClicks: uniqueClicksSet.size,
        bounces,
        unsubscribes,
        pendingRetry
      });
    });
    
    return statsMap;
  }, [campaigns, events]);

  const handleDuplicate = async (campaign) => {
    try {
      const duplicated = {
        name: `${campaign.name} (Copy)`,
        fromName: campaign.fromName,
        fromEmail: campaign.fromEmail,
        replyTo: campaign.replyTo,
        templateId: campaign.templateId,
        segmentIds: campaign.segmentIds || [],
        status: 'draft',
        stats: {
          sent: 0,
          delivered: 0,
          opens: 0,
          uniqueOpens: 0,
          clicks: 0,
          uniqueClicks: 0,
          bounces: 0,
          unsubscribes: 0
        },
        createdBy: currentUser?.uid || '',
        createdByName: currentUser?.displayName || currentUser?.email || 'Unknown',
        lastEditedBy: currentUser?.uid || '',
        lastEditedByName: currentUser?.displayName || currentUser?.email || 'Unknown',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(doc(db, collectionPath('emailCampaigns'), generateTimestampId('campaign_copy')), duplicated);
      await logUserAction({ action: 'Email Campaign Duplicated', details: `Campaign duplicated: "${campaign.name}" → "Copy of ${campaign.name}"`, entityType: 'EmailCampaign', entityId: campaign.id });
      setToast({ message: 'Campaign duplicated successfully!', type: 'success' });
    } catch (error) {
      console.error('Duplicate error:', error);
      setToast({ message: 'Failed to duplicate campaign: ' + error.message, type: 'error' });
    }
  };

  const handleArchive = async (campaign) => {
    try {
      await updateDoc(doc(db, collectionPath('emailCampaigns'), campaign.id), {
        isArchived: !campaign.isArchived,
        updatedAt: serverTimestamp()
      });
      await logUserAction({ action: campaign.isArchived ? 'Email Campaign Restored' : 'Email Campaign Archived', details: `Campaign ${campaign.isArchived ? 'restored' : 'archived'}: "${campaign.name}"`, entityType: 'EmailCampaign', entityId: campaign.id });
      
      setToast({ 
        message: campaign.isArchived ? 'Campaign restored' : 'Campaign archived', 
        type: 'success' 
      });
    } catch (error) {
      console.error('Archive error:', error);
      setToast({ message: 'Failed to update campaign', type: 'error' });
    }
  };

  const handleDelete = async (campaign) => {
    const hasSentEmails = campaign.status === 'sent' && (campaign.stats?.sent || 0) > 0;
    
    const confirmMsg = hasSentEmails
      ? `⚠️ Delete Campaign & All Data ⚠️\n\n` +
        `"${campaign.name}" has ${campaign.stats?.sent || 0} sent emails.\n\n` +
        `This will permanently delete:\n` +
        `• The campaign itself\n` +
        `• All email event data (opens, clicks, etc.)\n` +
        `• All analytics for this campaign\n\n` +
        `Type "DELETE" to confirm:`
      : `⚠️ Delete Campaign ⚠️\n\n` +
        `Are you sure you want to delete "${campaign.name}"?\n\n` +
        `This action cannot be undone.`;

    if (hasSentEmails) {
      const input = window.prompt(confirmMsg);
      if (input !== 'DELETE') {
        if (input !== null) {
          setToast({ message: 'Deletion cancelled. You must type "DELETE" to confirm.', type: 'error' });
        }
        return;
      }
    } else {
      if (!window.confirm(confirmMsg)) return;
    }

    try {
      setToast({ message: `Deleting campaign "${campaign.name}"...`, type: 'info' });

      // Delete all related emailEvents in batches
      const eventsQuery = query(
        collection(db, collectionPath('emailEvents')),
        where('campaignId', '==', campaign.id)
      );
      const eventsSnap = await getDocs(eventsQuery);
      
      if (eventsSnap.size > 0) {
        const docs = eventsSnap.docs;
        for (let i = 0; i < docs.length; i += 500) {
          const batch = writeBatch(db);
          docs.slice(i, i + 500).forEach(d => batch.delete(d.ref));
          await batch.commit();
        }
      }

      // Delete the campaign document
      const { deleteDoc } = await import('firebase/firestore');
      await deleteDoc(doc(db, collectionPath('emailCampaigns'), campaign.id));
      
      await logUserAction({ action: 'Email Campaign Deleted', details: `Campaign deleted: "${campaign.name}"${eventsSnap.size > 0 ? ` (${eventsSnap.size} email events removed)` : ''}`, entityType: 'EmailCampaign', entityId: campaign.id });
      
      const eventsDeleted = eventsSnap.size;
      setToast({ 
        message: `Campaign "${campaign.name}" deleted${eventsDeleted > 0 ? ` (${eventsDeleted} email events removed)` : ''}`, 
        type: 'success' 
      });
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ message: 'Failed to delete campaign: ' + error.message, type: 'error' });
    }
  };

  const handleResetStatus = async (campaign) => {
    const confirmed = window.confirm(
      `Reset campaign status?\n\n` +
      `This will change the status from "${campaign.status}" back to "draft" so you can edit and resend it.\n\n` +
      `Are you sure?`
    );

    if (!confirmed) return;

    try {
      await updateDoc(doc(db, collectionPath('emailCampaigns'), campaign.id), {
        status: 'draft',
        error: null,
        updatedAt: serverTimestamp()
      });
      await logUserAction({ action: 'Email Campaign Status Reset', details: `Campaign "${campaign.name}" status reset from "${campaign.status}" to "draft"`, entityType: 'EmailCampaign', entityId: campaign.id });
      
      setToast({ 
        message: 'Campaign status reset to draft. You can now edit and resend it.', 
        type: 'success' 
      });
    } catch (error) {
      console.error('Reset status error:', error);
      setToast({ message: 'Failed to reset campaign status', type: 'error' });
    }
  };

  const stats = useMemo(() => {
    // Calculate YTD (Year To Date) for campaigns sent - includes ALL campaigns (active and archived)
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    
    // Count campaigns created YTD with status 'sent' or 'partial_failure'
    const campaignsSentYTD = campaigns.filter(c => {
      const createdAt = c.createdAt?.toDate?.();
      return createdAt && createdAt >= yearStart && (c.status === 'sent' || c.status === 'partial_failure');
    }).length;
    
    // Calculate total emails sent YTD (to recipients)
    const ytdCampaigns = campaigns.filter(c => {
      const createdAt = c.createdAt?.toDate?.();
      return createdAt && createdAt >= yearStart;
    });
    const totalEmailsSentYTD = ytdCampaigns.reduce((sum, c) => sum + (c.stats?.sent || 0), 0);
    
    // Other stats only include active campaigns
    const activeCampaigns = campaigns.filter(c => !c.isArchived);
    const totalOpens = activeCampaigns.reduce((sum, c) => sum + (c.stats?.uniqueOpens || 0), 0);
    const totalClicks = activeCampaigns.reduce((sum, c) => sum + (c.stats?.uniqueClicks || 0), 0);
    const totalBounces = activeCampaigns.reduce((sum, c) => sum + (c.stats?.bounces || 0), 0);
    const totalSentActive = activeCampaigns.reduce((sum, c) => sum + (c.stats?.sent || 0), 0);
    
    return {
      campaignsSentYTD,
      totalEmailsSentYTD,
      avgOpenRate: totalSentActive > 0 ? ((totalOpens / totalSentActive) * 100).toFixed(1) : 0,
      avgClickRate: totalSentActive > 0 ? ((totalClicks / totalSentActive) * 100).toFixed(1) : 0,
      bounceRate: totalSentActive > 0 ? ((totalBounces / totalSentActive) * 100).toFixed(1) : 0,
      activeCampaigns: activeCampaigns.length
    };
  }, [campaigns]);

  const handleCreateCampaign = () => {
    // Open builder with null campaign - ID will be generated when user saves
    setSelectedCampaign(null);
    setShowBuilder(true);
  };

  if (loading) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading campaigns...</div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Email Campaigns</h2>
          <p className="text-sm text-gray-500">{viewAllEmail ? 'Tenant-wide campaign access' : 'Your campaigns, limited to your currently assigned customers and contacts.'}</p>
        </div>
        {can(FEATURE_PERMISSIONS.EMAIL_CREATE_CAMPAIGN) && (
        <button
          onClick={handleCreateCampaign}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          Create Campaign
        </button>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="All non-archived campaigns">
            Active Campaigns
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold">{stats.activeCampaigns}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: 20-25%">
            Avg Open Rate
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold">{stats.avgOpenRate}%</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: 2-5%">
            Avg Click Rate
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold">{stats.avgClickRate}%</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Industry benchmark: <2%">
            Bounce Rate
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold">{stats.bounceRate}%</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-500 cursor-help flex items-center gap-1" title="Number of campaigns with 'sent' status created this year (includes active and archived)">
            Campaigns Sent YTD
            <span className="inline-flex items-center justify-center w-4 h-4 text-xs font-semibold text-white bg-gray-400 rounded-full">i</span>
          </div>
          <div className="text-2xl font-bold">{stats.campaignsSentYTD || 0}</div>
          <div className="text-xs text-gray-500 mt-1">
            {(stats.totalEmailsSentYTD || 0).toLocaleString()} emails sent
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-4 mb-4 border-b">
        {['active', 'draft', 'scheduled', 'sent', 'archived'].map(status => {
          // Calculate count for each tab based on creator filter
          let count = 0;
          let baseFilter = campaigns;
          
          if (fb.applied.creatorFilter === 'mine') {
            baseFilter = campaigns.filter(c => c.createdBy === currentUser?.uid);
          } else {
            baseFilter = campaigns.filter(c => !c.isPrivate || !c.createdBy || c.createdBy === currentUser?.uid);
          }
          
          if (status === 'active') {
            count = baseFilter.filter(c => !c.isArchived).length;
          } else if (status === 'archived') {
            count = baseFilter.filter(c => c.isArchived).length;
          } else if (status === 'sent') {
            count = baseFilter.filter(c => (c.status === 'sent' || c.status === 'partial_failure' || c.status === 'failed') && !c.isArchived).length;
          } else {
            count = baseFilter.filter(c => c.status === status && !c.isArchived).length;
          }
          
          return (
            <button
              key={status}
              onClick={() => fb.setDraft('filter', status)}
              className={`px-4 py-2 ${
                fb.draft.filter === status 
                  ? 'border-b-2 border-indigo-600 text-indigo-600 font-semibold' 
                  : 'text-gray-500'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
              {count > 0 && (
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${
                  fb.draft.filter === status ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-gray-600'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}  </div>

      {/* Search and Filter Controls */}
      <div className="mb-4 flex gap-4 items-center">
        <select
          value={fb.draft.creatorFilter}
          onChange={(e) => fb.setDraft('creatorFilter', e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="all">All Campaigns ({allCampaignsCount})</option>
          <option value="mine">My Campaigns ({myCampaignsCount})</option>
        </select>
        
        <input
          type="text"
          placeholder="Search campaigns by name, subject, or sender..."
          value={fb.draft.search}
          onChange={(e) => fb.setDraft('search', e.target.value)}
          onKeyDown={fb.applyOnEnter}
          className={`flex-1 px-4 py-2 border rounded-lg transition-colors duration-150 ${
            fb.draft.search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
          }`}
        />
        
        <FilterActions onApply={fb.apply} onClear={fb.clear} isDirty={fb.isDirty} hasActiveFilters={fb.hasActiveFilters} />
        
        {fb.applied.search && (
          <div className="text-sm text-gray-600 whitespace-nowrap">
            {filteredCampaigns.length} results
          </div>
        )}
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Campaign
                  {fb.draft.sortField === 'name' && (
                    <span className="text-indigo-600">{fb.draft.sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Segments</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Opens</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center gap-1">
                  Created Date
                  {fb.draft.sortField === 'createdAt' && (
                    <span className="text-indigo-600">{fb.draft.sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => handleSort('createdBy')}
              >
                <div className="flex items-center gap-1">
                  Created By
                  {fb.draft.sortField === 'createdBy' && (
                    <span className="text-indigo-600">{fb.draft.sortDirection === 'asc' ? '▲' : '▼'}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCampaigns.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-6 py-4 text-center text-gray-500">
                  No campaigns found. Create your first campaign to get started.
                </td>
              </tr>
            ) : (
              filteredCampaigns.map(campaign => {
                const stats = campaignStats.get(campaign.id) || { sent: 0, opens: 0, uniqueOpens: 0, clicks: 0, uniqueClicks: 0 };
                const template = templatesById.get(campaign.templateId);
                const campaignSegments = (campaign.segmentIds || []).map(id => segmentsById.get(id)).filter(Boolean);
                
                return (
                <tr key={campaign.id} className={`hover:bg-gray-50 ${campaign.isArchived ? 'bg-gray-50 opacity-60' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      <Tooltip text={
                        campaign.description ? (
                          <div className="text-left">
                            <div className="font-semibold mb-1">{campaign.name}</div>
                            <div className="text-sm text-gray-200">{campaign.description}</div>
                          </div>
                        ) : campaign.name
                      } maxWidth={400}>
                        <span>{campaign.name}</span>
                      </Tooltip>
                      {campaign.isPrivate && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300">
                          🔒 Private
                        </span>
                      )}
                      {campaign.isArchived && (
                        <span className="ml-2 text-xs text-gray-500 italic">(Archived)</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{campaign.subject}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {template ? (
                      <div>
                        <div className="text-gray-900">{template.name}</div>
                        {template.subject && (
                          <div className="text-xs text-gray-500">{template.subject}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">No template</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {campaignSegments.length > 0 ? (
                      <div className="flex flex-col space-y-1">
                        {campaignSegments.map(seg => (
                          <span key={seg.id} className="px-2 py-1 text-xs rounded bg-indigo-100 text-indigo-700 inline-block">
                            {seg.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">No segments</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      campaign.status === 'sent' ? 'bg-green-100 text-green-800' :
                      campaign.status === 'partial_failure' ? 'bg-orange-100 text-orange-800' :
                      campaign.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                      campaign.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                      campaign.status === 'sending' ? 'bg-yellow-100 text-yellow-800' :
                      campaign.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {campaign.status === 'partial_failure' ? 'partial failure' : campaign.status}
                    </span>
                    {campaign.status === 'sending' && campaign.recipientCount > 0 && (() => {
                      const processed = (stats.sent || 0) + (stats.failed || 0);
                      const total = campaign.recipientCount;
                      const pct = Math.min(100, Math.round((processed / total) * 100));
                      return (
                        <div className="mt-1 w-32">
                          <div className="flex justify-between text-[10px] text-gray-600 mb-0.5">
                            <span>{processed.toLocaleString()} / {total.toLocaleString()}</span>
                            <span>{pct}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-yellow-500 h-1.5 rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      );
                    })()}
                    {campaign.error && (
                      <div className="text-xs text-red-600 mt-1" title={campaign.error}>
                        ⚠️ {campaign.error.length > 60 ? campaign.error.substring(0, 60) + '...' : campaign.error}
                      </div>
                    )}
                    {stats.failed > 0 && campaign.status !== 'failed' && !campaign.error && (
                      <div className="text-xs text-orange-600 mt-1">
                        ⚠️ {stats.failed} failed
                      </div>
                    )}
                    {stats.pendingRetry > 0 && (
                      <div className="text-xs text-yellow-600 mt-1" title="Recipients deferred within the last 72 hours. SendGrid is still auto-retrying. After 72h these are reclassified as expired and removed from this count.">
                        ⏳ {stats.pendingRetry} pending retry
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">{stats.sent.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm">
                    {stats.opens} 
                    {stats.sent > 0 && (
                      <span className="text-gray-500 ml-1">
                        ({((stats.uniqueOpens / stats.sent) * 100).toFixed(1)}%)
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {stats.clicks}
                    {stats.sent > 0 && (
                      <span className="text-gray-500 ml-1">
                        ({((stats.uniqueClicks / stats.sent) * 100).toFixed(1)}%)
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {campaign.createdAt ? (
                      <div className="text-sm text-gray-900">
                        {new Date(campaign.createdAt.toDate()).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400">Unknown</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {campaign.createdByName || (campaign.createdBy ? 'Unknown User' : 'Legacy Campaign')}
                    </div>
                    {campaign.lastEditedBy && (
                      <div className="text-xs text-gray-500 mt-1">
                        Last edited by: {campaign.lastEditedByName || 'Unknown'}
                        {campaign.updatedAt && (
                          <span className="ml-1">
                            ({formatDistanceToNow(campaign.updatedAt.toDate(), { addSuffix: true })})
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm space-x-3">
                    <button
                      onClick={() => {
                        setSelectedCampaign(campaign);
                        setShowBuilder(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      {campaign.status === 'draft' ? 'Edit' : 'View'}
                    </button>
                    {(campaign.status === 'sent' || campaign.status === 'partial_failure' || campaign.status === 'failed') && (
                      <button
                        onClick={() => navigate(`/campaigns/${campaign.id}/analytics`)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Analytics
                      </button>
                    )}
                    {(campaign.status === 'sending' || campaign.status === 'failed' || campaign.status === 'partial_failure' || campaign.error) && can(FEATURE_PERMISSIONS.EMAIL_EDIT_CAMPAIGN) && (
                      <button
                        onClick={() => handleResetStatus(campaign)}
                        className="text-orange-600 hover:text-orange-900"
                        title="Reset to draft to fix and resend"
                      >
                        Reset
                      </button>
                    )}
                    {can(FEATURE_PERMISSIONS.EMAIL_EDIT_CAMPAIGN) && (
                    <>
                    <button
                      onClick={() => handleDuplicate(campaign)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Duplicate campaign"
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => handleArchive(campaign)}
                      className={campaign.isArchived ? "text-green-600 hover:text-green-900" : "text-gray-600 hover:text-gray-900"}
                      title={campaign.isArchived ? "Restore campaign" : "Archive campaign"}
                    >
                      {campaign.isArchived ? 'Restore' : 'Archive'}
                    </button>
                    {((campaign.status === 'sent' && can(FEATURE_PERMISSIONS.EMAIL_DELETE_SENT_CAMPAIGN)) ||
                     (campaign.status !== 'sent' && can(FEATURE_PERMISSIONS.EMAIL_DELETE_CAMPAIGN))) && (
                      <button
                        onClick={() => handleDelete(campaign)}
                        className="text-red-600 hover:text-red-900"
                        title={campaign.status === 'sent' ? "Delete campaign and all email events" : "Delete campaign"}
                      >
                        Delete
                      </button>
                    )}
                    </>
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Campaign Builder Modal */}
      {showBuilder && (
        <CampaignBuilder
          campaign={selectedCampaign}
          onClose={() => {
            setShowBuilder(false);
            setSelectedCampaign(null);
          }}
          onSaved={(message) => {
            setShowBuilder(false);
            setSelectedCampaign(null);
            setToast({ message, type: 'success' });
          }}
        />
      )}
    </main>
  );
};

export default EmailCampaignPage;